<template>
   <article class="legal-doc">
      <h1>Termini e Condizioni di SpotFish</h1>
      <p class="legal-updated">Ultimo aggiornamento: 30 maggio 2024</p>

      <h2>Introduzione e ambito di applicazione</h2>
      <p>
         Queste condizioni stabiliscono le condizioni vincolanti per l'utilizzo della piattaforma e
         i rapporti legali con l'operatore. I termini con iniziale maiuscola ricevono definizioni
         specifiche nelle sezioni designate.
      </p>
      <p>
         Gli utenti sono tenuti a leggere attentamente. Contratti aggiuntivi tra l'operatore e i
         venditori prevalgono su queste disposizioni. Se la distribuzione è avvenuta tramite Apple
         App Store, Apple può esercitare diritti derivati come beneficiario terzo.
      </p>

      <h2>Informazioni sul fornitore del servizio</h2>
      <p>
         Filtex 1965 Distribution SAS<br />
         Via Mario Colletta 15, 13811 Andorno Micca (BI), Italia<br />
         Email: <a href="mailto:info@spotfish.app">info@spotfish.app</a>
      </p>

      <h2>Riepilogo rapido</h2>
      <ul>
         <li>
            Restrizioni di età: minimo 18 anni, con supervisione dei genitori richiesta per i minori
         </li>
         <li>
            Gli abbonamenti prevedono il rinnovo automatico soggetto a specifiche procedure di
            cancellazione
         </li>
         <li>Si applicano regole diverse per utenti consumatori e utenti commerciali</li>
         <li>Restrizioni di accesso basate su embargo geografico e liste di sanzioni</li>
      </ul>

      <h2>Come funziona SpotFish</h2>

      <h3>Funzione di agenzia</h3>
      <p>
         La piattaforma identifica prodotti dai venditori corrispondenti alle esigenze degli utenti
         attraverso la presentazione selettiva dei fornitori. L'operatore riceve un beneficio
         economico esclusivamente al completamento delle transazioni, raccogliendo commissioni come
         specificato.
      </p>

      <h3>Rilascio e indennizzo dell'utente</h3>
      <p>
         Accettando questi termini, gli utenti rilasciano incondizionatamente l'operatore da
         rivendicazioni riguardanti controversie, utilizzo del prodotto, violazioni di proprietà
         intellettuale, capacità dell'utente di transare o responsabilità per uso improprio del
         prodotto.
      </p>

      <h3>Verifica e rimozione dei contenuti</h3>
      <p>
         Le procedure di verifica dell'identità operano senza garanzie. L'operatore si riserva la
         discrezione assoluta di rimuovere contenuti censurabili senza preavviso. Gli utenti
         rinunciano a rivendicazioni derivanti da tali rimozioni.
      </p>

      <h3>Responsabilità della conservazione dei dati</h3>
      <p>
         L'operatore non ha responsabilità per la conservazione dei materiali inviati dagli utenti.
         Gli utenti devono mantenere indipendentemente copie di backup dei contenuti pubblicati.
      </p>

      <h3>Struttura del servizio</h3>
      <p>
         SpotFish funziona come infrastruttura tecnica che consente l'interazione tra utenti. I
         venditori determinano i termini delle transazioni, le condizioni e le specifiche dei
         prodotti in modo indipendente. L'operatore non controlla, valuta, monitora o ispeziona i
         prodotti o gli acquirenti.
      </p>

      <h2>Venditori della piattaforma</h2>
      <p>I prodotti provengono da terze parti che operano come utenti commerciali.</p>

      <h2>Condizioni di utilizzo</h2>

      <h3>Requisiti generali</h3>
      <p>Tutti gli utenti devono:</p>
      <ul>
         <li>Avere almeno 18 anni</li>
         <li>Non risiedere in giurisdizioni sotto embargo USA</li>
         <li>Non comparire nelle liste di sponsor del terrorismo designate dagli USA</li>
         <li>Non essere nelle liste di esclusione dei contraenti USA</li>
      </ul>

      <h3>Registrazione dell'account</h3>
      <h4>Requisiti di registrazione</h4>
      <ul>
         <li>Creazione automatica di account vietata</li>
         <li>Un singolo account per utente (salvo diversa indicazione)</li>
         <li>Account non trasferibile senza consenso espresso</li>
      </ul>

      <h4>Sicurezza delle credenziali</h4>
      <p>
         Gli utenti devono mantenere riservate le informazioni di accesso utilizzando la massima
         sicurezza disponibile. Sono completamente responsabili delle attività dell'account e devono
         notificare immediatamente l'operatore di compromissioni sospette tramite i metodi di
         contatto documentati.
      </p>

      <h4>Chiusura dell'account</h4>
      <p>Gli utenti possono terminare gli account in qualsiasi momento contattando l'operatore.</p>

      <h4>Sospensione/eliminazione dell'account</h4>
      <p>
         L'operatore si riserva la discrezionalità di sospendere o eliminare account ritenuti
         inappropriati o in violazione dei termini senza preavviso. La sospensione non fornisce
         diritti di compensazione, rimborso o indennizzo. L'eliminazione dell'account da parte
         dell'operatore non rinuncia agli obblighi di pagamento dell'utente.
      </p>

      <h2>Contenuti su SpotFish</h2>

      <h3>Contenuti controllati dall'operatore</h3>
      <p>
         Tutti i contenuti, salvo diversa indicazione, appartengono all'operatore o ai suoi
         licenzianti. L'operatore presta attenzione nel prevenire violazioni di norme o diritti di
         terzi, sebbene i risultati rimangano imperfetti.
      </p>
      <p><strong>Azioni vietate per gli utenti:</strong></p>
      <p>
         Gli utenti non possono copiare, scaricare, modificare, tradurre, pubblicare, trasmettere,
         vendere, sublicenziare, trasferire o creare opere derivate dai contenuti, salvo quanto
         esplicitamente autorizzato per scopi personali e non commerciali con corretta attribuzione.
      </p>

      <h3>Contenuti inviati dagli utenti</h3>
      <h4>Dichiarazioni dell'utente</h4>
      <p>
         All'invio dei contenuti, gli utenti confermano l'autorizzazione legale e che i materiali
         non violano leggi o diritti di terzi.
      </p>

      <h4>Diritti dell'operatore</h4>
      <p>
         Gli utenti concedono all'operatore diritti non esclusivi e gratuiti di elaborare i
         contenuti per scopi operativi e di manutenzione. Nei limiti della legge applicabile, gli
         utenti rinunciano all'esercizio dei diritti morali.
      </p>

      <h4>Responsabilità dei contenuti degli utenti</h4>
      <p>
         Gli utenti sono esclusivamente responsabili del materiale caricato. L'operatore non filtra
         né modera le pubblicazioni.
      </p>

      <h4>Discrezionalità di rimozione</h4>
      <p>
         L'operatore si riserva diritti di rimozione, cancellazione o blocco alla scoperta di
         violazioni, ricezione di avvisi di violazione IP, segnalazioni di violazione della privacy,
         ordini dell'autorità o identificazione di rischi per gli utenti o la disponibilità del
         servizio.
      </p>

      <h4>Indennizzo dell'utente</h4>
      <p>
         La rimozione non giustifica rivendicazioni di compensazione. Gli utenti devono indennizzare
         l'operatore contro rivendicazioni derivanti dai contenuti inviati. I contenuti contestati
         vengono rimossi entro 24 ore con negazione dell'accesso all'account per gli utenti
         responsabili.
      </p>

      <h2>Risorse esterne</h2>
      <p>
         Gli utenti possono accedere a risorse di terze parti. L'operatore non esercita alcun
         controllo e non assume responsabilità per il loro contenuto o disponibilità. I termini e le
         condizioni delle terze parti governano tali risorse.
      </p>

      <h2>Uso consentito</h2>
      <p>
         SpotFish può essere utilizzato esclusivamente per le finalità previste secondo i termini e
         la legge applicabile. Gli utenti sono i soli responsabili di garantire la conformità ai
         requisiti legali e ai diritti di terzi.
      </p>

      <h3>Condotte vietate</h3>
      <h4>Identità e impersonificazione</h4>
      <ul>
         <li>Falsificare il soddisfacimento dei requisiti di accesso</li>
         <li>Nascondere la propria identità o impersonare altri</li>
         <li>Alterare identificatori per oscurare l'origine dei messaggi</li>
      </ul>

      <h4>Condotta dannosa</h4>
      <ul>
         <li>Diffamare, minacciare o abusare di altri</li>
         <li>Promuovere autolesionismo, abuso di sostanze o violenza</li>
         <li>Pubblicare contenuti che esaltano traumi o atteggiamenti distruttivi</li>
      </ul>

      <h4>Abuso tecnico</h4>
      <ul>
         <li>Testare vulnerabilità del sistema</li>
         <li>Aggirare meccanismi di sicurezza</li>
         <li>Installare malware</li>
         <li>Abuso eccessivo dell'infrastruttura</li>
         <li>Interrompere la disponibilità del servizio</li>
      </ul>

      <h4>Estrazione di dati</h4>
      <p>
         Raccolta automatizzata di informazioni vietata senza autorizzazione espressa
         dell'operatore.
      </p>

      <h4>Contenuti illegali</h4>
      <ul>
         <li>Pubblicare materiale osceno, illegale o diffamatorio</li>
         <li>Promuovere odio, razzismo, discriminazione, pornografia o violenza</li>
         <li>Distribuire informazioni false</li>
         <li>Violazione dei diritti di proprietà intellettuale</li>
         <li>Violare diritti di terzi</li>
         <li>Compromettere l'integrità della piattaforma tramite spam, phishing, frode, malware</li>
      </ul>

      <h4>Sfruttamento degli utenti</h4>
      <ul>
         <li>Uso improprio di altri account</li>
         <li>Estrarre informazioni identificative</li>
         <li>Violare le impostazioni sulla privacy</li>
         <li>Uso improprio dei dati personali</li>
      </ul>

      <h4>Uso commerciale improprio</h4>
      <ul>
         <li>Gestire account per promozione di prodotti/servizi</li>
         <li>Dichiarare falsamente affiliazione o approvazione della piattaforma</li>
      </ul>

      <h3>Applicazione da parte dell'operatore</h3>
      <p>
         In caso di sospetta violazione, l'operatore può negare l'accesso, terminare contratti o
         segnalare attività illegali alle autorità competenti.
      </p>

      <h2>Programma referral</h2>
      <p>
         Gli utenti ricevono benefici quando le parti referenziate acquistano tramite i codici
         forniti. Ogni codice può essere riscattato una volta. Gli operatori si riservano la
         discrezionalità di terminazione. Il conteggio dei referral non ha limiti, sebbene possano
         applicarsi tetti ai benefici. Gli operatori alternativi possono limitare l'applicabilità
         del codice a prodotti selezionati.
      </p>

      <h2>Termini e condizioni di vendita</h2>

      <h3>Fornitura di dati personali</h3>
      <p>
         L'accesso a determinati prodotti richiede l'invio di dati personali come indicato su
         SpotFish.
      </p>

      <h3>Prodotti a pagamento</h3>
      <p>
         Tariffe, durata e condizioni appaiono nelle rispettive sezioni dei prodotti, soggette a
         modifiche senza preavviso.
      </p>

      <h3>Descrizione dei prodotti</h3>
      <p>
         Le presentazioni impiegano la massima accuratezza tecnica; le rappresentazioni visive
         rimangono riferimenti non vincolanti in assenza di garanzie caratteristiche. Le specifiche
         dei prodotti vengono dichiarate durante l'acquisto. I prezzi e la disponibilità sono
         soggetti a modifiche senza preavviso.
      </p>

      <h3>Procedura di acquisto</h3>
      <p>
         Il processo completo dalla selezione all'invio costituisce la procedura. Gli utenti
         selezionano i prodotti, verificano le scelte e inviano gli ordini.
      </p>

      <h3>Invio dell'ordine</h3>
      <p>
         L'invio completa il contratto, obbligando gli utenti a pagare prezzi, tasse e costi
         aggiuntivi. Gli acquisti dipendenti da contenuti creano obblighi di cooperazione
         dell'utente. La conferma dell'ordine viene inviata all'email dell'utente.
      </p>

      <h3>Prezzi</h3>
      <p>
         Le notifiche della procedura di acquisto dettagliano tutte le commissioni, tasse e costi
         prima dell'invio dell'ordine. I prezzi indicati includono commissioni applicabili, tasse e
         costi.
      </p>

      <h3>Promozioni e sconti</h3>
      <p>
         L'operatore può offrire promozioni speciali e sconti soggetti a requisiti e termini
         specificati. Tali promozioni operano a discrezione dell'operatore. Promozioni ripetute o
         periodiche non creano diritti futuri vincolanti.
      </p>
      <h4>Regole sui voucher</h4>
      <p>I voucher (codici promozionali o scontati) seguono specifiche condizioni di riscatto:</p>
      <ul>
         <li>Validi solo secondo i metodi e i tempi di utilizzo specificati</li>
         <li>Riscattabili interamente, non parzialmente</li>
         <li>Limitazione a uso singolo (uno per acquisto, compresi i pagamenti a rate)</li>
         <li>Non cumulabili</li>
         <li>Annullamento automatico alla scadenza</li>
         <li>Nessun rimborso per differenze di valore</li>
         <li>Uso esclusivamente non commerciale</li>
         <li>Riproduzione e commercializzazione severamente vietate</li>
      </ul>

      <h3>Metodi di pagamento</h3>
      <p>
         I metodi di pagamento accettati appaiono durante l'acquisto. Alcuni metodi possono
         richiedere condizioni aggiuntive o comportare costi extra.
      </p>
      <p>
         Servizi di pagamento di terze parti gestiscono le transazioni in modo autonomo. SpotFish
         riceve notifiche di successo senza accedere ai dati della carta di credito. Gli utenti
         possono riferirsi alla privacy policy per i dettagli sul trattamento dei dati personali.
      </p>
      <p>
         In caso di mancato pagamento o rifiuto del fornitore, gli operatori non hanno obbligo di
         esecuzione. Gli operatori si riservano diritti di rimborso per spese o danni correlati.
      </p>

      <h3>Riserva di proprietà</h3>
      <p>
         Gli utenti non acquisiscono diritti di proprietà fino al completo ricevimento del
         pagamento.
      </p>

      <h3>Riserva dei diritti di utilizzo</h3>
      <p>
         Gli utenti non acquisiscono diritti di utilizzo fino al completo ricevimento del pagamento.
      </p>

      <h3>Consegna di contenuti digitali</h3>
      <p>
         I contenuti digitali vengono scaricati sul dispositivo selezionato dall'utente. Gli utenti
         confermano che i dispositivi possiedono status legale, software aggiornato e conformità
         agli standard di mercato. Le possibilità di download sono soggette a limitazioni temporali
         e spaziali.
      </p>

      <h2>Durata del contratto</h2>

      <h3>Periodi di prova</h3>
      <p>
         Gli utenti possono accedere a SpotFish o a prodotti specifici gratuitamente durante periodi
         di prova non rinnovabili. Alcune funzionalità potrebbero rimanere non disponibili. Le
         condizioni specifiche della prova, inclusa la durata, appaiono su SpotFish.
      </p>
      <p>
         Alla scadenza, le prove si convertono automaticamente in prodotti a pagamento equivalenti
         salvo che gli utenti annullino prima della scadenza.
      </p>

      <h3>Abbonamenti</h3>
      <h4>Abbonamenti a tempo illimitato</h4>
      <p>
         Gli abbonamenti iniziano al ricevimento del pagamento. L'attività continuata richiede il
         pagamento periodico tempestivo; il mancato pagamento può interrompere il servizio.
      </p>

      <h4>Abbonamenti a tempo limitato</h4>
      <p>
         Gli abbonamenti iniziano al ricevimento del pagamento e rimangono attivi per la durata
         specificata dall'utente o indicata dalla procedura. L'accesso al prodotto cessa alla
         scadenza.
      </p>

      <h4>Rinnovo automatico (a tempo limitato)</h4>
      <p>
         Gli abbonamenti si rinnovano automaticamente con addebiti sul metodo di pagamento scelto
         dall'utente. La durata del rinnovo corrisponde al periodo di abbonamento originale.
      </p>
      <p>
         Gli utenti ricevono promemoria di rinnovo anticipati con dettagli sulle procedure di
         disattivazione.
      </p>

      <h4>Cancellazione (a tempo limitato)</h4>
      <p>
         Comunicazioni di cancellazione chiare e inequivocabili all'operatore tramite i dettagli di
         contatto elencati nel documento o le istruzioni di SpotFish completano la terminazione. Le
         cancellazioni ricevute prima delle date di rinnovo terminano gli abbonamenti alla scadenza
         del periodo corrente.
      </p>

      <h4>Eccezione per i consumatori (a tempo limitato)</h4>
      <p>
         Al completamento del termine iniziale, gli abbonamenti si convertono automaticamente in
         termini indefiniti salvo che gli utenti annullino in precedenza.
      </p>

      <h4>Addebiti di rinnovo</h4>
      <p>
         Gli addebiti alle tariffe precedenti si applicano tramite il metodo di pagamento originale.
      </p>

      <h4>Termini post-conversione</h4>
      <p>
         Gli abbonamenti indefiniti consentono la terminazione mensile. Gli utenti ricevono avvisi
         anticipati che descrivono le procedure di disattivazione.
      </p>

      <h4>Cancellazione dei consumatori (post-conversione)</h4>
      <p>
         Comunicazioni di cancellazione chiare e inequivocabili all'operatore tramite i dettagli di
         contatto elencati nel documento o le istruzioni di SpotFish completano la terminazione. Le
         cancellazioni ricevute entro la fine del mese corrente terminano gli abbonamenti alla
         scadenza del mese corrente.
      </p>

      <h4>Terminazione (abbonamenti indefiniti)</h4>
      <p>
         Comunicazioni di cancellazione chiare e inequivocabili all'operatore tramite i dettagli di
         contatto elencati nel documento o le istruzioni di SpotFish completano la terminazione. La
         terminazione diventa effettiva 30 giorni dopo il ricevimento della cancellazione da parte
         dell'operatore.
      </p>

      <h2>Diritti degli utenti</h2>

      <h3>Diritto di recesso</h3>
      <p>
         Gli utenti possono recedere dai contratti entro i periodi specificati (tipicamente 14
         giorni) per qualsiasi motivo senza giustificazione, soggetto a eccezioni.
      </p>
      <p>
         I diritti di recesso si applicano esclusivamente ai contratti con i venditori, non ai
         contratti con l'operatore. Le condizioni di recesso specifiche del venditore appaiono su
         SpotFish per ogni fornitore.
      </p>

      <h3>Diritti degli utenti nel Regno Unito</h3>
      <p>
         I consumatori del Regno Unito possono recedere dai contratti a distanza entro 14 giorni in
         assenza di eccezioni.
      </p>

      <h3>Diritti degli utenti brasiliani</h3>
      <p>
         I consumatori brasiliani possiedono diritti di recesso di 7 giorni dai contratti a distanza
         o fuori dai locali commerciali, contati dalla data di esecuzione del contratto o di
         ricevimento del prodotto/servizio.
      </p>

      <h2>Garanzie</h2>

      <h3>Conformità dei prodotti digitali nell'UE</h3>
      <p>
         Per un periodo minimo di 2 anni dalla consegna, o continuativamente per i prodotti forniti
         in modo continuativo per tutta la durata della fornitura, i venditori garantiscono la
         conformità dei prodotti digitali alle specifiche del consumatore secondo le norme europee.
      </p>
      <p>
         I consumatori dell'UE ricevono garanzie di conformità legali secondo le leggi del paese di
         residenza, che possono fornire protezioni superiori.
      </p>

      <h4>Obbligo di installazione degli aggiornamenti</h4>
      <p>
         Gli acquirenti di prodotti digitali devono installare/implementare gli aggiornamenti
         forniti dall'operatore secondo le istruzioni. La mancata installazione degli aggiornamenti
         può escludere le rivendicazioni di conformità.
      </p>

      <h3>Diritti di conformità dei consumatori nel Regno Unito</h3>
      <p>
         I consumatori del Regno Unito possiedono diritti di ricevere beni conformi ai contratti.
      </p>

      <h3>Garanzia dei prodotti brasiliani</h3>
      <p>
         La garanzia dei prodotti brasiliani segue i termini del Codice di Difesa del Consumatore:
      </p>
      <ul>
         <li>Prodotti non durevoli: garanzia di 30 giorni</li>
         <li>Prodotti durevoli: garanzia di 90 giorni</li>
         <li>I periodi di garanzia iniziano dalla consegna</li>
         <li>
            L'uso improprio, gli eventi naturali o l'assistenza non dell'operatore escludono la
            copertura
         </li>
         <li>
            L'operatore sostiene i costi di spedizione per la valutazione a propria discrezione
         </li>
         <li>L'operatore può fornire garanzie contrattuali supplementari alle protezioni legali</li>
      </ul>

      <h3>Supporto post-vendita</h3>
      <p>
         Gli acquirenti di prodotti ricevono supporto post-vendita gratuito per tutta la durata del
         periodo di garanzia tramite i dettagli di contatto dell'operatore.
      </p>

      <h2>Limitazioni di responsabilità e indennizzo</h2>

      <h3>Quadro generale</h3>
      <p>
         La responsabilità dell'operatore per l'esecuzione dell'accordo esclude, limita o riduce
         entro i massimi di legge applicabile.
      </p>

      <h3>Utenti europei</h3>
      <h4>Indennizzo</h4>
      <p>
         Gli utenti difendono, indennizzano e tengono indenni l'operatore e i subordinati,
         affiliati, funzionari, agenti, partner co-brand, partner e dipendenti secondo la legge
         applicabile da rivendicazioni di terzi derivanti da: violazioni dei termini, violazioni dei
         diritti di terzi, violazioni di legge, condotta dell'utente e rivendicazioni relative a
         prodotti digitali dal contenuto fornito dall'utente.
      </p>

      <h4>Limitazione delle attività</h4>
      <p>
         Gli utenti riconoscono che gli operatori forniscono infrastruttura tecnica e funzioni
         esclusivamente. Gli operatori non partecipano come intermediari, moderatori o promotori di
         transazioni, declinando responsabilità per le interazioni degli utenti, gli obblighi di
         completamento delle transazioni o le prestazioni degli utenti.
      </p>
      <p>
         Le vendite contrattuali tra venditori e acquirenti operano in modo indipendente; gli
         operatori non esercitano alcun coinvolgimento.
      </p>

      <h4>Limitazione dei danni</h4>
      <p>
         Le rivendicazioni di danni contro gli operatori sono soggette a esclusione completa secondo
         la legge applicabile, esclusi: morte, lesioni all'integrità personale/mentale, violazioni
         degli obblighi contrattuali essenziali, danni da dolo o colpa grave e danni tipicamente
         prevedibili secondo il tipo di contratto.
      </p>
      <p>Gli operatori declinano specificamente responsabilità per:</p>
      <ul>
         <li>
            Forza maggiore (guasti dell'infrastruttura, blackout, interruzioni di rete,
            inaccessibilità, scioperi, calamità naturali, virus, attacchi informatici, interruzioni
            di servizi di terze parti)
         </li>
         <li>Violazioni non dirette</li>
         <li>
            Profitti non realizzati, danni reputazionali, perdite aziendali, perdite contrattuali
         </li>
         <li>
            Danni da virus/malware da file scaricabili da internet (gli utenti mantengono la
            responsabilità antivirus/firewall)
         </li>
      </ul>

      <h4>Limitazione per utenti non consumatori</h4>
      <p>
         Le rivendicazioni di danni non possono eccedere gli importi di pagamento di 12 mesi o il
         totale dell'accordo (il minore dei due).
      </p>

      <h3>Utenti australiani</h3>
      <h4>Preservazione dei diritti statutari</h4>
      <p>
         Nessun termine esclude, limita o modifica garanzie, condizioni, indennizzi o diritti ai
         sensi del Competition and Consumer Act 2010 (Cth) o della legislazione statale/territoriale
         equivalente che costituiscono diritti non escludibili.
      </p>

      <h3>Utenti USA</h3>
      <h4>Esclusione di garanzia</h4>
      <p>
         SpotFish opera "così com'è" e "come disponibile". L'utilizzo del servizio avviene a rischio
         dell'utente. Gli operatori escludono espressamente garanzie di commerciabilità, idoneità e
         non violazione. Nessun consiglio orale/scritto crea garanzie implicite.
      </p>
      <p>
         Gli operatori declinano garanzie di accuratezza, affidabilità, continuità, prevenzione dei
         tempi di inattività, correzione dei difetti e assenza di virus. L'accesso al materiale
         scaricato avviene a rischio dell'utente.
      </p>

      <h4>Limitazione di responsabilità</h4>
      <p>
         Gli operatori declinano responsabilità per danni indiretti, consequenziali, punitivi,
         incluse perdite di profitto, di avviamento, di dati, di uso, di attività/ricavi, di
         contratti e danni reputazionali.
      </p>
      <p>
         La responsabilità non supera gli importi di pagamento di 12 mesi o la durata dell'accordo
         (il minore dei due).
      </p>

      <h4>Indennizzo</h4>
      <p>
         Gli utenti difendono, indennizzano e tengono indenni gli operatori da rivendicazioni di
         terzi e spese derivanti da: uso/accesso al servizio, violazioni dei termini, violazioni dei
         diritti di terzi, violazioni di legge/regolamento, contenuto dell'account dell'utente, dolo
         dell'utente e violazioni di legge da parte di affiliati.
      </p>

      <h2>Disposizioni comuni</h2>

      <h3>Nessuna rinuncia implicita</h3>
      <p>
         Il mancato esercizio di diritti non costituisce rinuncia. Rinunce specifiche a un diritto
         non creano implicazioni per altri diritti.
      </p>

      <h3>Interruzione del servizio</h3>
      <p>
         Gli operatori possono interrompere il servizio per manutenzione, aggiornamenti o modifiche
         con ragionevole preavviso.
      </p>
      <p>
         Gli operatori si riservano diritti di sospensione o cessazione completa del servizio. In
         caso di cessazione, gli operatori facilitano l'estrazione dei dati personali e rispettano i
         diritti di continuazione del prodotto dell'utente e il risarcimento secondo la legge
         applicabile.
      </p>
      <p>
         L'indisponibilità del servizio da eventi di forza maggiore esclude la responsabilità
         dell'operatore. Gli operatori si impegnano a fornire un preavviso di 30 giorni ove
         possibile.
      </p>

      <h3>Divieto di rivendita del servizio</h3>
      <p>
         La riproduzione, duplicazione, copia, vendita, rivendita o sfruttamento non autorizzati di
         SpotFish/Servizio senza previo consenso scritto dell'operatore (diretto o tramite programmi
         di rivendita legittimi) è vietata.
      </p>

      <h3>Privacy policy</h3>
      <p>
         Le informazioni sul trattamento dei dati personali appaiono nella
         <NuxtLink :to="$localePath('/legal/privacy')">privacy policy</NuxtLink> di SpotFish.
      </p>

      <h3>Proprietà intellettuale</h3>
      <p>
         Gli operatori possiedono esclusivamente diritti di proprietà intellettuale e industriale
         (copyright, marchi, brevetti, modelli) relativi a SpotFish secondo la legge internazionale
         applicabile sulla proprietà intellettuale.
      </p>
      <p>
         Tutti i marchi — denominativi o figurativi — e i segni distintivi, denominazioni sociali,
         marchi di servizio, illustrazioni, immagini, loghi connessi a SpotFish rimangono proprietà
         dell'operatore/licenziante, protetti secondo la legge internazionale applicabile sulla
         proprietà intellettuale.
      </p>

      <h3>Modifiche ai termini</h3>
      <p>
         Gli operatori si riservano diritti di modifica in qualsiasi momento con adeguato preavviso.
         L'uso continuato del servizio implica l'accettazione dei termini aggiornati. Gli utenti in
         disaccordo devono cessare l'uso del servizio e possono rescindere l'accordo.
      </p>
      <p>
         Le versioni precedenti continuano a governare fino all'accettazione dell'utente,
         disponibili su richiesta. Le modifiche legalmente richieste ricevono preavviso con date di
         efficacia specificate.
      </p>

      <h3>Cessione del contratto</h3>
      <p>
         Gli operatori si riservano diritti di trasferire, cedere, novare o esternalizzare
         diritti/obblighi individuali o completi secondo questi termini, rispettando gli interessi
         legittimi dell'utente. Gli utenti non possono cedere/trasferire diritti/obblighi senza
         consenso scritto dell'operatore.
      </p>

      <h3>Contatti</h3>
      <p>
         Tutte le comunicazioni relative a SpotFish richiedono l'uso dei dettagli di contatto
         elencati in questo documento.
      </p>

      <h3>Separabilità</h3>
      <p>
         Disposizioni nulle/inefficaci ai sensi della legge applicabile non invalidano le
         disposizioni rimanenti, che rimangono valide/efficaci. Le parti cercheranno amichevolmente
         di identificare disposizioni sostitutive valide/efficaci. In caso di mancato accordo, la
         legge applicabile fornisce la sostituzione della disposizione (ove consentito/previsto). La
         nullità/invalidità di una specifica disposizione non invalida l'intero accordo, salvo che
         le disposizioni essenziali creino impossibilità o onere eccessivo per una delle parti.
      </p>

      <h2>Legge applicabile e giurisdizione</h2>

      <h3>Legge applicabile</h3>
      <p>
         I termini operano sotto la giurisdizione della sede dell'operatore nonostante le regole di
         conflitto di leggi. La legge di protezione del consumatore del paese dell'Utente prevale se
         fornisce protezione superiore.
      </p>

      <h3>Giurisdizione esclusiva</h3>
      <p>
         La competenza esclusiva per le controversie deriva dalla giurisdizione della sede
         dell'operatore. Non si applica ai consumatori europei o ai consumatori nel Regno Unito,
         Svizzera, Norvegia o Islanda. Non si applica agli utenti qualificati come consumatori
         brasiliani.
      </p>

      <h2>Risoluzione delle controversie</h2>

      <h3>Risoluzione amichevole</h3>
      <p>
         Gli utenti possono segnalare controversie agli operatori per tentata risoluzione
         amichevole. Questo rimane non pregiudizievole per i diritti di contenzioso.
      </p>
      <p>
         Gli utenti inviano reclami tramite l'email dell'operatore elencata, includendo brevi
         descrizioni e dettagli dell'ordine/acquisto/account. Gli operatori rispondono alle
         richieste entro 30 giorni.
      </p>

      <h3>Piattaforma di risoluzione delle controversie dei consumatori dell'UE</h3>
      <p>
         I consumatori dell'UE e i residenti in Norvegia, Islanda o Liechtenstein possono accedere
         alla piattaforma di risoluzione alternativa delle controversie online della Commissione
         Europea all'indirizzo:
         <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener">
            https://ec.europa.eu/consumers/odr/
         </a>
      </p>

      <h2>Definizioni e riferimenti legali</h2>
      <dl>
         <dt>SpotFish (Applicazione)</dt>
         <dd>La struttura che consente l'erogazione del servizio.</dd>
         <dt>Accordo</dt>
         <dd>
            Qualsiasi rapporto giuridicamente vincolante o contratto tra operatore e utente secondo
            i termini.
         </dd>
         <dt>Utente Commerciale</dt>
         <dd>Qualsiasi utente che non si qualifica come consumatore.</dd>
         <dt>Acquirente</dt>
         <dd>
            Qualsiasi utente che acquista beni/servizi dell'operatore/fornitore tramite SpotFish
            indipendentemente dal luogo di completamento della transazione.
         </dd>
         <dt>Voucher</dt>
         <dd>Qualsiasi codice digitale/cartaceo che consente l'acquisto scontato di prodotti.</dd>
         <dt>Prodotto Digitale</dt>
         <dd>
            Contenuto prodotto/fornito in formato digitale e/o servizi che consentono creazione,
            trasformazione, archiviazione, accesso, condivisione o altra interazione con dati
            digitali caricati/creati da utenti/altri utenti di SpotFish.
         </dd>
         <dt>Operatore (Noi)</dt>
         <dd>L'entità che fornisce SpotFish e/o offre il servizio agli utenti.</dd>
         <dt>Prodotto</dt>
         <dd>
            Qualsiasi bene/servizio disponibile tramite SpotFish inclusi beni materiali, file
            digitali, software, servizi di prenotazione e altri tipi di prodotti definiti
            separatamente inclusi prodotti digitali.
         </dd>
         <dt>Venditore</dt>
         <dd>
            Qualsiasi utente che vende beni/servizi agli acquirenti tramite SpotFish
            indipendentemente dal luogo di completamento della transazione.
         </dd>
         <dt>Servizio</dt>
         <dd>
            Il servizio offerto tramite SpotFish come descritto nei termini e nella presentazione di
            SpotFish.
         </dd>
         <dt>Termini</dt>
         <dd>
            Tutte le condizioni che governano l'uso di SpotFish e/o l'erogazione del servizio
            secondo questo documento e qualsiasi documento/accordo connesso nella versione corrente.
         </dd>
         <dt>Utente (Tu)</dt>
         <dd>Qualsiasi persona che utilizza SpotFish.</dd>
         <dt>Consumatore</dt>
         <dd>Qualsiasi utente considerato consumatore ai sensi della legge applicabile.</dd>
      </dl>

      <p class="legal-footer">
         Salvo diversa indicazione, questi termini riguardano esclusivamente SpotFish.
      </p>
   </article>
</template>

<style scoped>
@reference '../../assets/css/main.css';

.legal-doc {
   @apply p-6 sm:p-10 md:p-14 lg:p-20;
}

.legal-doc h1 {
   @apply h-xxl mb-6;
}

.legal-doc h2 {
   @apply h-xl mb-4 mt-10;
}

.legal-doc h3 {
   @apply h-l mb-3 mt-6;
}

.legal-doc h4 {
   @apply h-m mb-2 mt-4;
}

.legal-doc p {
   @apply p-m text-grey mb-3;
}

.legal-doc ul {
   @apply p-m text-grey mb-4 list-disc pl-6;
}

.legal-doc li {
   @apply mb-1;
}

.legal-doc dl {
   @apply p-m text-grey mt-4;
}

.legal-doc dt {
   @apply mt-4 font-bold text-black;
}

.legal-doc dd {
   @apply mb-2 pl-4;
}

.legal-doc a {
   @apply text-primary underline;
}

.legal-updated {
   @apply p-s text-grey mb-6;
}

.legal-footer {
   @apply p-s text-grey border-light-grey mt-10 border-t pt-6;
}
</style>
